import { useCallback, useState } from "react";
import { useContentModals } from "../contexts/contentModalsContext";
import { useCompetitorScopeGuard } from "./useCompetitorScopeGuard";
import { useBlockIfSubscriptionPaywall } from "./useIsSubscriptionPaywallActive";
import useOutsideCampaignGallery from "./useOutsideCampaignGallery";
import { buildCompetitorImportInitialValues } from "../utils/competitorReferenceImport";

/**
 * Shared competitor-scope flow: guard → ModalCompetitors → ModalCreateWithIA.
 * Used by the sidebar menu and the Dashboard home features.
 */
export const useOpenCompetitorScopeFlow = ({
  brandInformation,
  onBeforeOpen,
} = {}) => {
  const { openCreateWithIA } = useContentModals();
  const { onOpenGallery } = useOutsideCampaignGallery();
  const blockIfPaywall = useBlockIfSubscriptionPaywall();
  const [isCompetitorsModalOpen, setIsCompetitorsModalOpen] = useState(false);
  const [initialPreviewLibraryID, setInitialPreviewLibraryID] = useState(null);

  const { onTryOpenCompetitorScope } = useCompetitorScopeGuard({
    brandInformation,
  });

  const openCompetitorScope = useCallback(
    ({ initialPreviewLibraryID: previewLibraryID } = {}) => {
      if (blockIfPaywall()) return;

      onBeforeOpen?.();

      if (previewLibraryID) {
        setInitialPreviewLibraryID(String(previewLibraryID));
      }

      onTryOpenCompetitorScope({
        onOpen: () => setIsCompetitorsModalOpen(true),
      });
    },
    [blockIfPaywall, onBeforeOpen, onTryOpenCompetitorScope]
  );

  const onCloseCompetitorsModal = useCallback(() => {
    setIsCompetitorsModalOpen(false);
    setInitialPreviewLibraryID(null);
  }, []);

  const onInitialPreviewLibraryIDConsumed = useCallback(() => {
    setInitialPreviewLibraryID(null);
  }, []);

  const onCompetitorAdsSelected = useCallback(
    (items) => {
      const selectedItem = items?.[0];
      if (!selectedItem?.url) return;

      openCreateWithIA({
        initialValues: buildCompetitorImportInitialValues({
          url: selectedItem.url,
          type: selectedItem.type,
        }),
        onCallbackAfterSaveToGallery: onOpenGallery,
      });
    },
    [onOpenGallery, openCreateWithIA]
  );

  return {
    openCompetitorScope,
    onCompetitorAdsSelected,
    modalsProps: {
      isCompetitorsModalOpen,
      onCloseCompetitorsModal,
      onCompetitorAdsSelected,
      initialPreviewLibraryID,
      onInitialPreviewLibraryIDConsumed,
    },
  };
};
